// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { Check } from "lucide-react"
import { hasAppAccess, isFreeTierPlan } from "@bitebalance/contracts"
import type {
  BillingOffer,
  BillingOffersResponse,
  BillingSubscriptionResponse,
  MeResponse,
} from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { useAuth } from "@/hooks/useAuth"
import { accessMode, continuationHint, freeGrantFailure, subscriptionPanel } from "@/lib/accessView"
import { trackFunnelEvent } from "@/lib/analytics"
import {
  checkoutRefusalCode,
  checkoutRefusalMessageKey,
  shouldFallBackToStaticCheckout,
  type CheckoutRefusalCode,
} from "@/lib/checkoutRefusal"
import { Button } from "@/components/ui/button"
import { ErrorState, LoadingState } from "@/components/state"
import { useI18n } from "@/i18n"

/**
 * /app/access — plan activation (T17 follow-up, owner request 2026-08-23).
 * Post-onboarding landing and the More → "Plan & billing" page. Shows the
 * server's entitlement truthfully (never granted from a redirect): the
 * user opens the hosted Whop checkout, the signed webhook grants the
 * entitlement, and this page polls GET /v1/me until the server confirms.
 *
 * ADR-083 (2026-09-25): also the engine behind AUTOMATIC free-access
 * activation. useOnboardingFlow.ts navigates here with
 * `state.autoActivateFreeClaim` right after the plan is generated; this
 * page silently runs the exact same (card-free) checkout a manual "Claim
 * your 2 weeks free" click already triggers below, shows a brief "ready"
 * confirmation once the webhook confirms it, then continues to
 * /app/plan-ready. Nothing here grants an entitlement directly — the
 * signed webhook still does, unchanged (see `activate` below).
 *
 * ADR-085 IAP-4 (2026-10-04): while the server retires the Whop checkout it
 * answers `offers.appStoreOnly`; this page then sells NOTHING (no checkout,
 * no price, no "coming soon" row): the 14-day free period is claimed through
 * our own server (`POST /v1/billing/free-access`, then a /v1/me re-read) and a
 * paid plan is bought in the BiteBalance iPhone app. The subscription panel
 * also follows the row's provider (lib/accessView.ts): an Apple row shows no
 * price and no Cancel button, a server-granted free period shows "Free" and
 * an ends line, and only a Whop row keeps today's panel with its Cancel
 * button (existing Whop members must be able to cancel). With `appStoreOnly`
 * absent every Whop branch below behaves exactly as before
 * (src/test/access-whop-mode-golden.test.tsx pins the DOM).
 */
const POLL_INTERVAL_MS = 4000
const POLL_MAX_MS = 3 * 60 * 1000
/** Apple's own subscription-management page (the same URL the iPhone app opens). */
const APPLE_MANAGE_SUBSCRIPTIONS_URL = "https://apps.apple.com/account/subscriptions"

/**
 * "Claimed" means a provider-granted (individual/b2b) entitlement — the
 * automatic no-charge pilot grant every tester holds does NOT count,
 * otherwise the free-plan claim card would never be shown to anyone.
 */
function isActive(me: MeResponse): boolean {
  return (
    (me.entitlement.source === "individual" || me.entitlement.source === "b2b") &&
    (me.entitlement.state === "active" || me.entitlement.state === "trialing")
  )
}

function formatPrice(offer: BillingOffer, locale: string): string {
  const eur = new Intl.NumberFormat(locale === "de" ? "de-DE" : "en-DE", {
    style: "currency",
    currency: "EUR",
  }).format(offer.priceEurCents / 100)
  return eur
}

export default function AccessPage() {
  const { t, locale } = useI18n()
  const api = useApiClient()
  const { email } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // ADR-083: set only on the client-side navigate() from onboarding —
  // never present after the hard reload to/from Whop, which is fine: that
  // return is identified separately below via the `plan` query param.
  const autoActivateIntent = Boolean(
    (location.state as { autoActivateFreeClaim?: boolean } | null)?.autoActivateFreeClaim
  )
  const [autoActivateUnavailable, setAutoActivateUnavailable] = useState(false)
  const autoActivateFiredRef = useRef(false)

  const [me, setMe] = useState<MeResponse | null>(null)
  const [offers, setOffers] = useState<BillingOffersResponse | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [claiming, setClaiming] = useState(false)
  // ADR-085 IAP-4: true once THIS page started a server-side free-period grant (never in Whop mode). It plays the role
  // `isFreeClaimReturn` plays for the Whop round trip: an "activating" moment, then the confirmation, then plan-ready.
  const [serverGrantStarted, setServerGrantStarted] = useState(false)
  const [freeStartNoticeKey, setFreeStartNoticeKey] = useState<string | null>(null)
  const [pollTimedOut, setPollTimedOut] = useState(false)
  // ADR-085 client refusal rule: set when the server refuses to start a Whop checkout; the page then
  // says what is true instead of opening any checkout link.
  const [checkoutRefusal, setCheckoutRefusal] = useState<CheckoutRefusalCode | null>(null)
  const pollStop = useRef<(() => void) | null>(null)
  // ADR-032c in-app cancellation state machine.
  const [cancelState, setCancelState] = useState<
    | { phase: "idle" }
    | { phase: "confirming" }
    | { phase: "working" }
    | { phase: "done"; accessUntil: string | null }
    | { phase: "error" }
  >({ phase: "idle" })

  const handleCancel = useCallback(async () => {
    setCancelState({ phase: "working" })
    try {
      const res = await api.cancelBillingSubscription()
      setCancelState({ phase: "done", accessUntil: res.accessUntil })
      // Refresh the panel so the scheduled cancellation shows immediately.
      api
        .getBillingSubscription()
        .then(setSubInfo)
        .catch(() => {})
    } catch {
      setCancelState({ phase: "error" })
    }
  }, [api])

  // ADR-032c: Claude-style plan detail (trial end / renewal date / scheduled
  // cancellation), fetched only when an individual plan is active. All dates
  // come from the provider via the server — never computed client-side.
  const [subInfo, setSubInfo] = useState<BillingSubscriptionResponse | null>(null)
  const activeIndividual =
    me !== null &&
    (me.entitlement.source === "individual" || me.entitlement.source === "b2b") &&
    (me.entitlement.state === "active" || me.entitlement.state === "trialing")
  // ADR-085 IAP-4: while the server sells nothing on the web, an account WITHOUT access is read too — a paused (past-due, after
  // grace) Apple subscription must say so instead of offering a plan that cannot be bought here. Whop mode: unchanged (active only).
  const mode = offers ? accessMode(offers) : null
  const readSubscription = activeIndividual || (mode?.kind === "app_store_only" && me !== null)
  useEffect(() => {
    if (!readSubscription) return
    let cancelled = false
    api
      .getBillingSubscription()
      .then(info => {
        if (!cancelled) setSubInfo(info)
      })
      .catch(() => {
        /* panel degrades to the basic state line */
      })
    return () => {
      cancelled = true
    }
  }, [api, readSubscription])

  /** "€9.99/month", "€24.99 / 3 months", "€79.99/year" — price alone is ambiguous across plans. */
  const priceWithPeriod = useCallback(
    (offer: BillingOffer) => {
      const base = formatPrice(offer, locale)
      const suffix =
        offer.billingPeriod === "month"
          ? t("access.perMonth")
          : offer.billingPeriod === "quarter"
            ? t("access.perQuarter")
            : offer.billingPeriod === "year"
              ? t("access.perYear")
              : ""
      return `${base}${suffix}`
    },
    [locale, t]
  )

  const formatDate = useCallback(
    (iso: string) =>
      new Date(iso).toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    [locale]
  )

  const load = useCallback(async () => {
    setLoadError(false)
    try {
      const [meRes, offersRes] = await Promise.all([api.getMe(), api.getBillingOffers()])
      setMe(meRes)
      setOffers(offersRes)
    } catch {
      setLoadError(true)
    }
  }, [api])

  useEffect(() => {
    void load()
    return () => pollStop.current?.()
  }, [load])

  // ADR-032: Whop's checkout session redirects the buyer straight back here
  // with ?checkout=return. The webhook grant may still be in flight, so start
  // polling immediately and show the confirming state — the user never has to
  // find their way back or refresh manually. The param is scrubbed so a later
  // reload doesn't restart a stale poll.
  const [searchParams, setSearchParams] = useSearchParams()
  const returnedFromCheckout = searchParams.get("checkout") === "return"
  // Captured ONCE, before the scrub effect below deletes both params (and
  // `returnedFromCheckout` itself goes stale — it's re-derived from the
  // now-scrubbed searchParams on the next render). ADR-083's confirmation
  // UI needs to stay keyed on "was this a freeClaim return" for the rest
  // of this page's life, not just the first render.
  const [returnedPlanKey] = useState(() => searchParams.get("plan"))
  const [isFreeClaimReturn] = useState(
    () => searchParams.get("checkout") === "return" && searchParams.get("plan") === "freeClaim"
  )
  useEffect(() => {
    if (returnedFromCheckout) {
      setClaiming(true)
      // G04/ADR-083: the browser is back on our domain — Whop's own
      // checkout UI is done. This does NOT mean the entitlement is granted
      // yet (the poll below waits for that); it only means the visitor
      // completed Whop's side of the flow. `planKey` distinguishes the
      // automatic free-access round trip from a real paid conversion —
      // both fire this same event.
      trackFunnelEvent("whop_flow_completed", { planKey: returnedPlanKey ?? undefined })
      startPolling()
      const next = new URLSearchParams(searchParams)
      next.delete("checkout")
      next.delete("plan")
      setSearchParams(next, { replace: true })
    }
  }, [returnedFromCheckout])

  const startPolling = useCallback(() => {
    pollStop.current?.()
    setPollTimedOut(false)
    const startedAt = Date.now()
    const timer = window.setInterval(() => {
      if (Date.now() - startedAt > POLL_MAX_MS) {
        window.clearInterval(timer)
        setClaiming(false)
        setPollTimedOut(true)
        return
      }
      api
        .getMe()
        .then(meRes => {
          setMe(meRes)
          if (isActive(meRes)) {
            window.clearInterval(timer)
            setClaiming(false)
          }
        })
        .catch(() => {
          /* transient poll failure — keep polling until timeout */
        })
    }, POLL_INTERVAL_MS)
    pollStop.current = () => window.clearInterval(timer)
  }, [api])

  const activate = useCallback(
    async (offer: BillingOffer) => {
      if (!offer.checkoutUrl) return
      setClaiming(true)
      trackFunnelEvent("checkout_started", { planKey: offer.planKey })
      // ADR-032: ask the server for a per-user checkout session — it binds
      // the purchase to this account via metadata (no email matching needed)
      // and carries the redirect back to this page. Fall back to the offer's
      // static link if the session call fails; checkout is never blocked —
      // EXCEPT when the server REFUSES it (ADR-085 never-bill-twice: a live
      // Apple subscription, a used free period, or the retired Whop checkout).
      // A page that loaded its offers before an Apple purchase still holds
      // this static link; opening it would be a real second charge.
      let checkoutUrl = offer.checkoutUrl
      try {
        const res = await api.createBillingCheckout({ planKey: offer.planKey })
        checkoutUrl = res.checkoutUrl
      } catch (err) {
        if (!shouldFallBackToStaticCheckout(err)) {
          // Refusal: open NO link, say what is true, and reload the offers so
          // the page shows the real state. A refused auto-activation degrades
          // to the normal page rather than spinning forever.
          setCheckoutRefusal(checkoutRefusalCode(err))
          setAutoActivateUnavailable(true)
          setClaiming(false)
          void load()
          return
        }
        /* fall back to the static link */
      }
      // Prefill the signed-in email so Whop's checkout doesn't ask again
      // (owner feedback 2026-08-24); Whop ignores unknown params harmlessly.
      const url = email
        ? `${checkoutUrl}${checkoutUrl.includes("?") ? "&" : "?"}email=${encodeURIComponent(email)}`
        : checkoutUrl
      // SAME tab (ADR-032): the checkout session's redirect_url brings the
      // buyer straight back to /app/access?checkout=return, so the journey is
      // linear — no orphaned tab, no manual way back.
      window.location.assign(url)
    },
    [api, email, load]
  )

  // ADR-085 IAP-4: the free period while the server sells nothing on the web is OUR OWN grant — one POST, then a /v1/me re-read
  // (the confirmation moment keys on isActive(me)). No Whop checkout, no link, no poll, no `checkout_started`. A failure says what is
  // true and reloads the offers, so a used period stops being offered.
  const claimFreeAccess = useCallback(async () => {
    setServerGrantStarted(true)
    setFreeStartNoticeKey(null)
    setClaiming(true)
    try {
      await api.grantFreeAccess()
      const meRes = await api.getMe()
      setMe(meRes)
      if (!isActive(meRes)) setAutoActivateUnavailable(true)
      void load() // the offers now say the period is used
    } catch (err) {
      const failure = freeGrantFailure(err)
      if (failure === "freeUsed") setCheckoutRefusal("FREE_PERIOD_ALREADY_USED")
      else setFreeStartNoticeKey(failure === "featureDisabled" ? "access.apple.featureDisabled" : "access.web.freeStartFailed")
      setAutoActivateUnavailable(true)
      void load()
    } finally {
      setClaiming(false)
    }
  }, [api, load])

  // ADR-083: the automatic activation itself — runs once `me`/`offers` have
  // loaded, reusing `activate()` verbatim (same checkout session, same
  // webhook grant, same poll). Never fires more than once per mount, and
  // degrades to the normal offer UI (autoActivateUnavailable=true) rather
  // than blocking on anything — an unavailable/failed freeClaim offer must
  // never strand a user who just finished onboarding.
  // ADR-085 IAP-4: while the server sells nothing on the web, the same moment is the server grant
  // (`claimFreeAccess`) — only while the free period is still claimable.
  useEffect(() => {
    if (!autoActivateIntent || autoActivateFiredRef.current || !me || !offers) return
    if (isActive(me)) {
      autoActivateFiredRef.current = true
      setAutoActivateUnavailable(true)
      return
    }
    const storeOnly = accessMode(offers)
    if (storeOnly.kind === "app_store_only") {
      autoActivateFiredRef.current = true
      if (!storeOnly.freePeriodClaimable) {
        setAutoActivateUnavailable(true)
        return
      }
      trackFunnelEvent("trial_started")
      void claimFreeAccess()
      return
    }
    const freeClaimOffer = offers.offers.find(o => o.planKey === "freeClaim" && o.available)
    if (!freeClaimOffer) {
      autoActivateFiredRef.current = true
      setAutoActivateUnavailable(true)
      return
    }
    autoActivateFiredRef.current = true
    trackFunnelEvent("trial_started")
    void activate(freeClaimOffer)
  }, [autoActivateIntent, me, offers, activate, claimFreeAccess])

  // True for the whole window between "just triggered the auto-checkout"
  // (or a manual freeClaim claim) and either a confirmed grant or a timeout.
  const showActivatingMoment =
    (isFreeClaimReturn && claiming) ||
    (serverGrantStarted && claiming) ||
    (autoActivateIntent && !autoActivateUnavailable && !isFreeClaimReturn && !(me && isActive(me)))

  const freeClaimConfirmed = (isFreeClaimReturn || serverGrantStarted) && !claiming && me !== null && isActive(me)

  const paywallViewedFiredRef = useRef(false)
  useEffect(() => {
    if (paywallViewedFiredRef.current || !me || !offers) return
    if (isFreeClaimReturn || showActivatingMoment) return
    paywallViewedFiredRef.current = true
    // Verified (2026-09-25): only fires once the real choose-a-plan surface
    // is about to render — never during automatic activation, which shows
    // no offer/paywall UI at all.
    trackFunnelEvent("paywall_viewed")
  }, [me, offers, isFreeClaimReturn, showActivatingMoment])

  const trialActivatedFiredRef = useRef(false)
  useEffect(() => {
    if (trialActivatedFiredRef.current || !freeClaimConfirmed) return
    trialActivatedFiredRef.current = true
    trackFunnelEvent("trial_activated")
  }, [freeClaimConfirmed])

  // Short confirmation moment (founder-specified copy, ADR-083), then
  // straight into the generated meal plan — no further paywall stop.
  useEffect(() => {
    if (!freeClaimConfirmed) return
    const timer = window.setTimeout(() => {
      navigate("/app/plan-ready", { replace: true, state: { alreadyActivated: true } })
    }, 1800)
    return () => window.clearTimeout(timer)
  }, [freeClaimConfirmed, navigate])

  // Independent review finding (2026-09-13, blocker): `RequireOnboardingComplete`
  // is a layout route — it does NOT remount on a client-side `navigate()`
  // between its own children. A user who lands here with no access, gains it
  // (the poll above updates local `me`), then presses Continue via a plain
  // `navigate()` would re-render the SAME guard instance, which never
  // reran its own /v1/me check and is still holding its stale "noAccess"
  // verdict from before this page's poll resolved — the guard immediately
  // bounces back to /app/access, in a loop, until a hard reload. A hard
  // navigation forces the guard to remount and re-check with current
  // server truth, the same reason `activate()` above already uses
  // `window.location.assign` rather than client-side navigation to Whop.
  // (`pendingPlanJobId` was router-state-only and TodayPage.tsx has never
  // read it — nothing carries forward here, unlike mobile's own route param.)
  const continueToToday = useCallback(() => {
    // G04: only the pilot/free-tier branch — a real paid subscriber (active
    // individual/b2b) landing here from More → "Plan & billing" is not an
    // acquisition-funnel step, just navigation; subscription_purchased
    // already covers that path server-side.
    if (me && !isActive(me)) trackFunnelEvent("app_continued_free")
    window.location.assign("/app/today")
  }, [me])

  /**
   * One paid-offer card, used at both places this page lists paid plans
   * (the upgrade path for an existing free-tier/cancelled subscriber, and
   * the plain paid ladder for a never-subscribed visitor) — was duplicated
   * verbatim at both call sites until code review flagged it; mobile's
   * `access.tsx` already had this factored into one `paidOfferRow`.
   */
  const paidOfferRow = (offer: BillingOffer) => (
    <div
      key={offer.planKey}
      className="flex flex-wrap items-center justify-between gap-3 rounded-bb-xl border border-line bg-paper px-5 py-4"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="flex items-center gap-2 font-semibold text-ink">
          {t(`access.planName.${offer.planKey}`)}
          {offer.trialDays > 0 && (
            <span className="rounded-bb-full bg-green/10 px-2 py-0.5 text-xs font-semibold text-green">
              {t("access.trialBadge")}
            </span>
          )}
        </span>
        <span className="text-sm text-ink-2">
          {offer.trialDays > 0
            ? t("access.trialNote", { price: priceWithPeriod(offer) })
            : priceWithPeriod(offer)}
        </span>
      </div>
      <Button
        type="button"
        variant="outline"
        className="min-h-11"
        disabled={claiming}
        onClick={() => void activate(offer)}
      >
        {/* Item 4 removed the Whop-side trial from all three paid plans —
            "Start free trial" would now be a false promise (bills
            immediately). trialDays > 0 keeps the old copy correct if a
            trial ever returns. */}
        {offer.trialDays > 0 ? t("access.choosePlan") : t("access.subscribeNow")}
      </Button>
    </div>
  )

  if (loadError) {
    return (
      <section aria-labelledby="access-heading" className="flex flex-col gap-4">
        <h1 id="access-heading" className="font-display text-3xl font-bold tracking-tight text-ink">
          {t("access.title")}
        </h1>
        <ErrorState message={t("access.loadError")} onRetry={() => void load()} />
      </section>
    )
  }
  if (!me || !offers) {
    return (
      <section aria-labelledby="access-heading" className="flex flex-col gap-4">
        <h1 id="access-heading" className="font-display text-3xl font-bold tracking-tight text-ink">
          {t("access.title")}
        </h1>
        <LoadingState label={t("common.loading")} />
      </section>
    )
  }

  // ADR-083: the automatic-activation moment — no offer list, no paywall,
  // just an honest "setting this up" state while the (card-free) checkout
  // and its webhook grant are in flight.
  if (showActivatingMoment) {
    return (
      <section aria-labelledby="access-heading" className="flex flex-col items-center gap-4 py-16 text-center">
        <h1 id="access-heading" className="sr-only">
          {t("access.title")}
        </h1>
        <span
          aria-hidden="true"
          className="size-10 animate-spin rounded-full border-4 border-line border-t-green"
        />
        <p role="status" aria-live="polite" className="text-sm text-ink-2">
          {t("access.freeClaimActivating")}
        </p>
      </section>
    )
  }

  // ADR-083: the short confirmation moment (exact founder wording), then an
  // automatic continue to /app/plan-ready — see the useEffect above.
  if (freeClaimConfirmed) {
    return (
      <section aria-labelledby="access-heading" className="flex flex-col items-center gap-3 py-16 text-center">
        <h1 id="access-heading" className="sr-only">
          {t("access.title")}
        </h1>
        <span
          aria-hidden="true"
          className="flex size-12 items-center justify-center rounded-full bg-green text-paper"
        >
          <Check className="size-6" />
        </span>
        <p role="status" aria-live="polite" className="max-w-sm text-lg font-semibold text-ink">
          {t("access.freeClaimReady")}
        </p>
      </section>
    )
  }

  const active = isActive(me)
  // Item 8 step 7 (2026-09-12): this page is now a REQUIRED stop for
  // anyone without app access (RequireOnboardingComplete redirects here)
  // — `hasAppAccess` (unlike `active` above) also counts a grandfathered
  // pilot grant, so an existing pilot holder opening this page from More
  // still sees Continue immediately.
  const canContinue = hasAppAccess(me)
  // Item 8 (2026-09-12): the hero card is the 2-week free claim while the
  // flag keeps it in the offers (replaces the retired pre-launch free
  // month, ADR-032). Paid plans render below.
  //
  // Ultrareview nit (2026-09-13): a `planKey === "free"` fallback used to
  // sit here for the long-retired sandbox plan — dead even before this
  // task (confirmed: `main`'s own `PLAN_KEYS` already excluded "free" from
  // GET /v1/billing/offers), removed rather than left as a branch nobody
  // can exercise or test. `"free"` remains a valid WIRE value (a legacy
  // subscriber's own `GET /v1/billing/subscription` can still report it),
  // just never an offer in this array.
  const heroOffer = offers.offers.find(o => o.planKey === "freeClaim" && o.available) ?? null
  const paidOffers = offers.offers.filter(o => o.available && o.planKey !== "freeClaim")
  const futureOffers = offers.offers.filter(o => !o.available)
  const heroIsFreeClaim = heroOffer?.planKey === "freeClaim"

  // ADR-085 IAP-4: what the server sells here, and which provider bills the subscription row (both pure: lib/accessView.ts).
  const storeOnly = accessMode(offers)
  const appStoreOnly = storeOnly.kind === "app_store_only"
  const panel = subInfo ? subscriptionPanel(subInfo, me, Date.now()) : null
  const applePanel = panel?.kind === "apple" ? panel : null
  const hint = continuationHint(storeOnly, subInfo)
  const hintDate = subInfo?.currentPeriodEnd ? formatDate(subInfo.currentPeriodEnd) : null
  // The Whop payment / invoices / cancellation blocks describe a Whop membership: never for an Apple row or a server grant, and
  // (while the server sells nothing here) only once the row is KNOWN to be a Whop row.
  const showWhopBlocks = applePanel === null && panel?.kind !== "grant" && (!appStoreOnly || panel?.kind === "whop")
  // A paused Apple subscription of an account that no longer has access: say so, with the way to fix it.
  const appleNeedsPaymentFix = !active && appStoreOnly && applePanel?.pastDue === "after_grace"

  return (
    <section aria-labelledby="access-heading" className="flex max-w-2xl flex-col gap-5">
      <div>
        <h1 id="access-heading" className="font-display text-3xl font-bold tracking-tight text-ink">
          {t("access.title")}
        </h1>
        <p className="mt-1 text-sm text-ink-2">{appStoreOnly ? t("access.web.subtitle") : t("access.subtitle")}</p>
      </div>

      {offers.pilotAccess && (
        <p className="rounded-bb-md border border-green/40 bg-green/5 px-4 py-3 text-sm text-ink">
          {t("access.pilotNote")}
        </p>
      )}

      {checkoutRefusal && (
        <div
          role="alert"
          className="flex flex-col gap-1 rounded-bb-md border border-line bg-paper px-4 py-3 text-sm text-ink"
        >
          <p>{t(checkoutRefusalMessageKey(checkoutRefusal))}</p>
          {checkoutRefusal === "APPLE_SUBSCRIPTION_ACTIVE" && (
            <a
              className="self-start font-semibold underline underline-offset-2"
              href={APPLE_MANAGE_SUBSCRIPTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("access.apple.manageCta")}
            </a>
          )}
        </div>
      )}

      {freeStartNoticeKey && (
        <p
          role="alert"
          className="rounded-bb-md border border-line bg-paper px-4 py-3 text-sm text-ink"
        >
          {t(freeStartNoticeKey)}
        </p>
      )}

      {returnedFromCheckout && claiming && (
        <p className="rounded-bb-md border border-green/40 bg-green/5 px-4 py-3 text-sm text-ink">
          {t("access.returnConfirming")}
        </p>
      )}

      {active ? (
        <>
          <div className="flex flex-col gap-2 rounded-bb-xl border border-green bg-green/5 p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold text-ink">
                  {subInfo
                    ? panel?.kind === "grant"
                      ? t("access.apple.freeAccessName")
                      : t(`access.planName.${subInfo.planKey}`)
                    : t("access.activeTitle")}
                </p>
                {/* State pill: TRIAL (lime) / ACTIVE (green) / ENDING (amber). */}
                {subInfo && (
                  <span
                    className={`rounded-bb-full px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase ${
                      subInfo.cancelAtPeriodEnd
                        ? "bg-amber-100 text-amber-900"
                        : subInfo.state === "trialing"
                          ? "bg-lime text-ink"
                          : "bg-green/15 text-green"
                    }`}
                  >
                    {subInfo.cancelAtPeriodEnd
                      ? t("access.badgeEnding")
                      : t(`access.state.${subInfo.state}`)}
                  </span>
                )}
              </div>
              {subInfo &&
                !applePanel &&
                (() => {
                  // Item 8: prelaunch/freeClaim are always €0 by definition
                  // and shown as "Free" without needing a matching offers-
                  // list entry — freeClaim replaces prelaunch as THE served
                  // offer going forward, so an existing prelaunch subscriber
                  // would otherwise find no match in `offers.offers` at all
                  // and silently show nothing.
                  if (isFreeTierPlan(subInfo.planKey)) {
                    return <p className="text-sm font-medium text-ink-2">{t("access.freePrice")}</p>
                  }
                  const offer = offers.offers.find(o => o.planKey === subInfo.planKey)
                  if (!offer) return null
                  return <p className="text-sm font-medium text-ink-2">{priceWithPeriod(offer)}</p>
                })()}
            </div>
            {/* Claude-style detail line per plan/state — provider-reported
                dates only, never computed client-side. */}
            {subInfo ? (
              (() => {
                const offer = offers.offers.find(o => o.planKey === subInfo.planKey)
                const price = offer ? priceWithPeriod(offer) : ""
                const date = subInfo.currentPeriodEnd ? formatDate(subInfo.currentPeriodEnd) : null
                if (applePanel) {
                  // ADR-085: an Apple row. Provider-reported dates only; never Whop's price (the web has no StoreKit price).
                  const line = applePanel.pastDue === "grace"
                    ? date
                      ? t("access.apple.paymentProblemGrace", { date })
                      : t(`access.state.${subInfo.state}`)
                    : applePanel.pastDue === "after_grace"
                      ? t("access.web.paymentProblemPaused")
                      : applePanel.ending
                        ? date
                          ? t("access.subCancelledLine", { date })
                          : t("access.cancelDoneNoDate")
                        : date
                          ? t("access.web.appleRenewsOn", { date })
                          : t("access.web.appleManagedLine")
                  return <p className="text-sm text-ink-2">{line}</p>
                }
                if (subInfo.cancelAtPeriodEnd || subInfo.state === "canceled") {
                  return (
                    <p className="text-sm text-ink-2">
                      {date ? t("access.subCancelledLine", { date }) : t("access.cancelDoneNoDate")}
                    </p>
                  )
                }
                if (subInfo.planKey === "prelaunch" || subInfo.planKey === "freeClaim") {
                  // Item 9.B follow-up (2026-09-14, in-app half of the
                  // founder's reminder request): the SAME `currentPeriodEnd`
                  // the backend already computes and serves (item 9.B's
                  // `expires_at` fallback fix) also drives this urgent
                  // variant — no new endpoint, no new poll, just reading a
                  // value already fetched on every load of this page. The
                  // email half is a separate, one-time scheduled reminder
                  // (`infra-cdk/lambdas/expiry-reminders`); this banner is
                  // live-computed and reappears every time the user looks,
                  // for as long as it's still true.
                  const hoursUntil = subInfo.currentPeriodEnd
                    ? (new Date(subInfo.currentPeriodEnd).getTime() - Date.now()) / 3_600_000
                    : null
                  const isUrgent =
                    panel?.kind === "grant" ? panel.urgent : hoursUntil !== null && hoursUntil > 0 && hoursUntil <= 24
                  // App Review 2026-10-30 (3.1.2(c)): the server-granted free access is the SAME copy the iPhone app shows — "Free
                  // access until <date>. This is not a subscription: no payment details, nothing renews, nothing is charged." —
                  // never the Whop free-claim lines ("pick a plan below"), which describe a plan row that does not exist here.
                  const grant = panel?.kind === "grant"
                  const key = grant
                    ? "access.apple.freeAccessLine"
                    : subInfo.planKey === "prelaunch"
                      ? "access.subPrelaunchLine"
                      : "access.subFreeClaimLine"
                  const noDateKey = grant
                    ? "access.apple.freeAccessLineNoDate"
                    : subInfo.planKey === "prelaunch"
                      ? "access.subPrelaunchLineNoDate"
                      : "access.subFreeClaimLineNoDate"
                  return (
                    <p className={`text-sm leading-relaxed ${isUrgent ? "font-semibold text-amber-700" : "text-ink"}`}>
                      {date ? t(isUrgent ? `${key}Urgent` : key, { date }) : t(noDateKey)}
                    </p>
                  )
                }
                if (subInfo.state === "trialing") {
                  return (
                    <p className="text-sm leading-relaxed text-ink">
                      {date
                        ? t("access.subTrialLine", { date, price })
                        : t(`access.state.${subInfo.state}`)}
                    </p>
                  )
                }
                return (
                  <p className="text-sm text-ink-2">
                    {date
                      ? t("access.subRenewLine", { date, price })
                      : t(`access.state.${subInfo.state}`)}
                  </p>
                )
              })()
            ) : (
              <p className="text-sm text-ink-2">
                {t(`access.source.${me.entitlement.source}`)} ·{" "}
                {t(`access.state.${me.entitlement.state}`)}
              </p>
            )}
          </div>

          {/* Upgrade path (owner request 2026-08-28): a pre-launch member —
              or anyone whose plan is already ending — can pick a paid plan
              RIGHT HERE, mid-month. Safe for these cases only: the current
              membership is free and/or already cancelled, so a new paid
              membership can never double-bill. Paid members with an ongoing
              subscription instead get the honest switch hint below. */}
          {subInfo && hint === "whop_rows" && paidOffers.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-ink-2">
                {subInfo.planKey === "prelaunch"
                  ? t("access.upgradeFromPrelaunchIntro")
                  : subInfo.planKey === "freeClaim"
                    ? t("access.upgradeFromFreeClaimIntro")
                    : t("access.paidIntro")}
              </p>
              {paidOffers.map(paidOfferRow)}
            </div>
          )}
          {/* ADR-085 IAP-4: the server sells nothing here, so "continue" is the iPhone app — one calm line, no button. */}
          {hint === "iphone_app" && (
            <div className="flex flex-col gap-1 rounded-bb-xl border border-line bg-paper p-5">
              <p className="text-sm text-ink">
                {hintDate ? t("access.web.continueInAppLine", { date: hintDate }) : t("access.web.continueInAppLineNoDate")}
              </p>
              {/* The note promises a free period: only a grant panel / free-tier plan has one (a Whop paid member who is ending does not). */}
              {(panel?.kind === "grant" || (subInfo && isFreeTierPlan(subInfo.planKey))) && (
                <p className="text-xs text-ink-2">{t("access.web.noIphoneNote")}</p>
              )}
            </div>
          )}
          {subInfo &&
            !appStoreOnly &&
            showWhopBlocks &&
            !isFreeTierPlan(subInfo.planKey) &&
            !subInfo.cancelAtPeriodEnd &&
            subInfo.state !== "canceled" && (
              <p className="text-xs text-ink-2">{t("access.switchPlanHint")}</p>
            )}

          {applePanel && (
            <>
              {/* ADR-085 IAP-4: Apple bills this subscription — Apple's payment copy, a Manage link, NO Cancel button
                  (cancellation lives in the Apple Account; the server would answer "managed by Apple" anyway). */}
              <div className="flex flex-col gap-1 rounded-bb-xl border border-line bg-paper p-5">
                <p className="font-semibold text-ink">{t("access.apple.paymentTitle")}</p>
                <p className="text-sm leading-relaxed text-ink-2">
                  {t("access.apple.paymentBody")}{" "}
                  <a
                    className="underline underline-offset-2 hover:text-ink"
                    href={`mailto:${t("landingPage.legal.contactEmail")}`}
                  >
                    {t("landingPage.legal.contactEmail")}
                  </a>
                  .
                </p>
              </div>
              <div className="flex flex-col gap-3 rounded-bb-xl border border-line bg-paper p-5">
                <p className="font-semibold text-ink">{t("access.apple.manageTitle")}</p>
                <p className="text-sm text-ink-2">{t("access.apple.manageBody")}</p>
                <a
                  className="self-start font-semibold underline underline-offset-2"
                  href={APPLE_MANAGE_SUBSCRIPTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("access.apple.manageCta")}
                </a>
              </div>
            </>
          )}

          {/* Payment + receipts — Claude-style sections, honest about where
              the data lives: Whop stores the card and emails every receipt;
              this key cannot read card details or list invoices, so we say
              so instead of showing an empty table. */}
          {/* No Whop-account links here (live-hit twice, 2026-08-28): the
              Whop portal needs a Whop login guest buyers don't have. Card
              updates happen via Whop's own secure email link when a charge
              fails; anything else goes through our support address. */}
          {showWhopBlocks && (
          <div className="flex flex-col gap-4 rounded-bb-xl border border-line bg-paper p-5">
            <div className="flex flex-col gap-1">
              <p className="font-semibold text-ink">{t("access.paymentTitle")}</p>
              <p className="text-sm leading-relaxed text-ink-2">
                {t("access.paymentBody")}{" "}
                <a
                  className="underline underline-offset-2 hover:text-ink"
                  href={`mailto:${t("landingPage.legal.contactEmail")}`}
                >
                  {t("landingPage.legal.contactEmail")}
                </a>
                .
              </p>
            </div>
            <div className="flex flex-col gap-1 border-t border-line pt-4">
              <p className="font-semibold text-ink">{t("access.invoicesTitle")}</p>
              <p className="text-sm text-ink-2">{t("access.invoicesBody")}</p>
            </div>
          </div>
          )}
          {showWhopBlocks && me.entitlement.source === "individual" && (
            <div className="flex flex-col gap-3 rounded-bb-xl border border-line bg-paper p-5">
              <p className="font-semibold text-ink">{t("access.manageTitle")}</p>
              <p className="text-sm text-ink-2">{t("access.manageBody")}</p>

              {/* ADR-032c: REAL in-app cancellation (server-side via the
                  provider admin API, always at period end). The old
                  portal-only link required a Whop login guest buyers don't
                  have — live-hit 2026-08-28. */}
              {cancelState.phase === "done" ? (
                <p className="rounded-bb-md border border-green/40 bg-green/5 px-4 py-3 text-sm text-ink">
                  {cancelState.accessUntil
                    ? t("access.cancelDone", {
                        date: new Date(cancelState.accessUntil).toLocaleDateString(
                          locale === "de" ? "de-DE" : "en-GB",
                          { day: "numeric", month: "long", year: "numeric" }
                        ),
                      })
                    : t("access.cancelDoneNoDate")}
                </p>
              ) : cancelState.phase === "confirming" || cancelState.phase === "working" ? (
                <div className="flex flex-col gap-3 rounded-bb-md border border-line bg-cream p-4">
                  <p className="text-sm text-ink">{t("access.cancelConfirmBody")}</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="destructive"
                      className="min-h-11"
                      disabled={cancelState.phase === "working"}
                      onClick={() => void handleCancel()}
                    >
                      {cancelState.phase === "working"
                        ? t("access.waitingForConfirmation")
                        : t("access.cancelConfirmCta")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="min-h-11"
                      disabled={cancelState.phase === "working"}
                      onClick={() => setCancelState({ phase: "idle" })}
                    >
                      {t("access.cancelKeep")}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11 self-start"
                  onClick={() => setCancelState({ phase: "confirming" })}
                >
                  {t("access.cancelCta")}
                </Button>
              )}
              {cancelState.phase === "error" && (
                <p className="text-sm text-destructive">{t("access.cancelError")}</p>
              )}
            </div>
          )}
        </>
      ) : storeOnly.kind === "app_store_only" ? (
        <>
          {/* ADR-085 IAP-4: nothing is sold on the web. The free period is our own server grant; a paid plan is bought in the
              iPhone app. No Whop card, no price, no "coming soon" row, no dead button. */}
          {appleNeedsPaymentFix && (
            <div className="flex flex-col gap-3 rounded-bb-xl border border-line bg-paper p-5">
              <p className="text-sm text-ink">{t("access.web.paymentProblemPaused")}</p>
              <a
                className="self-start font-semibold underline underline-offset-2"
                href={APPLE_MANAGE_SUBSCRIPTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("access.apple.manageCta")}
              </a>
            </div>
          )}
          {storeOnly.freePeriodClaimable ? (
            <div className="rounded-bb-xl border-2 border-green/50 bg-paper p-5">
              <p className="text-lg font-semibold text-ink">{t("access.web.freeStartTitle")}</p>
              <p className="mt-1 text-sm text-ink-2">{t("access.web.freeStartBody")}</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  className="min-h-11"
                  disabled={claiming}
                  onClick={() => void claimFreeAccess()}
                >
                  {claiming ? t("access.waitingForConfirmation") : t("access.web.freeStartCta")}
                </Button>
              </div>
            </div>
          ) : (
            !appleNeedsPaymentFix && (
              <p className="rounded-bb-md border border-line bg-paper px-4 py-3 text-sm text-ink-2">
                {t("access.apple.freeUsedLine")}
              </p>
            )
          )}
          <div className="flex flex-col gap-1 rounded-bb-xl border border-line bg-paper p-5">
            <p className="font-semibold text-ink">{t("access.web.appStoreOnlyTitle")}</p>
            <p className="text-sm leading-relaxed text-ink-2">{t("access.web.appStoreOnlyBody")}</p>
            {storeOnly.freePeriodClaimable && (
              <p className="mt-1 text-xs text-ink-2">{t("access.web.noIphoneNote")}</p>
            )}
          </div>
        </>
      ) : heroOffer ? (
        <div className="rounded-bb-xl border-2 border-green/50 bg-paper p-5">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-lg font-semibold text-ink">
              {heroIsFreeClaim ? t("access.freeClaimTitle") : t("access.freePlanTitle")}
            </p>
            <p className="text-lg font-bold text-ink">{formatPrice(heroOffer, locale)}</p>
          </div>
          <p className="mt-1 text-sm text-ink-2">
            {heroIsFreeClaim ? t("access.freeClaimBody") : t("access.freePlanBody")}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              className="min-h-11"
              disabled={claiming}
              onClick={() => void activate(heroOffer)}
            >
              {claiming
                ? t("access.waitingForConfirmation")
                : heroIsFreeClaim
                  ? t("access.activateFreeClaim")
                  : t("access.activateFree")}
            </Button>
            {claiming && !returnedFromCheckout && (
              <p className="text-sm text-ink-2">{t("access.checkoutHint")}</p>
            )}
          </div>
          {pollTimedOut && (
            <p className="mt-3 text-sm text-destructive">{t("access.pollTimedOut")}</p>
          )}
        </div>
      ) : (
        <p className="rounded-bb-md border border-line bg-paper px-4 py-3 text-sm text-ink-2">
          {t("access.noOffer")}
        </p>
      )}

      {!active && !appStoreOnly && paidOffers.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-ink-2">{t("access.paidIntro")}</p>
          {paidOffers.map(paidOfferRow)}
        </div>
      )}

      {/* Plans withheld from an Apple subscriber (never bill twice) are not "coming soon" — they are simply not offered here. */}
      {futureOffers.length > 0 && !appStoreOnly && !applePanel && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-ink-2">{t("access.comingSoonTitle")}</p>
          {futureOffers.map(offer => (
            <div
              key={offer.planKey}
              className="flex items-baseline justify-between rounded-bb-md border border-dashed border-line px-4 py-3 opacity-70"
            >
              <span className="text-sm text-ink">{t(`access.planName.${offer.planKey}`)}</span>
              <span className="text-sm text-ink-2">
                {formatPrice(offer, locale)} · {t("access.comingSoon")}
              </span>
            </div>
          ))}
        </div>
      )}

      {canContinue && (
        <Button
          type="button"
          variant={active ? "default" : "outline"}
          className="min-h-11 self-start"
          onClick={continueToToday}
        >
          {t("access.continue")}
        </Button>
      )}
    </section>
  )
}
